/**
 * CAT Profile Page (Hồ Sơ Năng Lực CAT)
 *
 * Matches Stitch design system exactly:
 * - Section 1: Bold amber→orange gradient banner with white text
 * - Section 2: Two separate white cards (exam table + personality results)
 * - Section 3: Slate-50 background, 3-column grid of grouped majors
 */

import { useState, useEffect, useCallback } from 'react';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Play,
  Lock,
  Share2,
  Copy,
  Check,
  X,
  LinkIcon,
} from 'lucide-react';
import {
  catProfileService,
  type CATEligibilityResult,
  type CATProfile,
} from '../services/catProfileService';
import { generateSlug } from '../data/majorsData';

const hollandDescriptions: Record<
  string,
  { label: string; english: string; color: string; barColor: string }
> = {
  group1: {
    label: 'KỸ THUẬT',
    english: 'Realistic',
    color: 'text-blue-600 dark:text-blue-400',
    barColor: 'bg-blue-500',
  },
  group2: {
    label: 'NGHIÊN CỨU',
    english: 'Investigative',
    color: 'text-purple-600 dark:text-purple-400',
    barColor: 'bg-purple-500',
  },
  group3: {
    label: 'NGHỆ THUẬT',
    english: 'Artistic',
    color: 'text-pink-600 dark:text-pink-400',
    barColor: 'bg-pink-500',
  },
  group4: {
    label: 'XÃ HỘI',
    english: 'Social',
    color: 'text-orange-600 dark:text-orange-400',
    barColor: 'bg-orange-500',
  },
  group5: {
    label: 'QUẢN LÝ',
    english: 'Enterprising',
    color: 'text-emerald-600 dark:text-emerald-400',
    barColor: 'bg-emerald-500',
  },
  group6: {
    label: 'NGHIỆP VỤ',
    english: 'Conventional',
    color: 'text-cyan-600 dark:text-cyan-400',
    barColor: 'bg-cyan-500',
  },
};

const hollandTexts: Record<string, string> = {
  group1: 'Thích làm việc với máy móc, công cụ, công việc thực tế, đòi hỏi sự khéo léo.',
  group2: 'Thích quan sát, tìm tòi, phân tích, đánh giá, giải quyết vấn đề.',
  group3: 'Yêu thích sáng tạo, nghệ thuật, tự do biểu đạt ý tưởng.',
  group4: 'Thích giúp đỡ, hướng dẫn, chăm sóc và phục vụ người khác.',
  group5: 'Thích lãnh đạo, thuyết phục, có khả năng giao tiếp và định hướng mục tiêu.',
  group6: 'Thích công việc có tổ chức, chi tiết, tuân thủ quy trình.',
};

export default function CATProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState<CATEligibilityResult | null>(null);
  const [profile, setProfile] = useState<CATProfile | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [eligibilityData, profileData] = await Promise.all([
        catProfileService.checkEligibility(),
        catProfileService.getProfile(),
      ]);

      setEligibility(eligibilityData);
      setProfile(profileData);
    } catch (err: any) {
      setError(err.message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getShareUrl = (token: string) => `${window.location.origin}/ho-so-nang-luc/s/${token}`;

  const handleShare = async () => {
    try {
      setSharing(true);
      const result = await catProfileService.shareProfile();
      setShareToken(result.shareToken);
      setShowShareModal(true);
    } catch (err: any) {
      setError(err.message || 'Không thể chia sẻ');
    } finally {
      setSharing(false);
    }
  };

  const handleDisableShare = async () => {
    try {
      await catProfileService.disableShare();
      setShareToken(null);
      setShowShareModal(false);
    } catch (err: any) {
      setError(err.message || 'Lỗi');
    }
  };

  const handleCopyLink = async () => {
    if (shareToken) {
      try {
        await navigator.clipboard.writeText(getShareUrl(shareToken));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        setCopied(false);
      }
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError(null);
      setInfoMessage(null);
      const result = await catProfileService.generateProfile();
      if ((result as any).noNewData) {
        setProfile(result);
        setInfoMessage(
          'Hồ sơ đã cập nhật với dữ liệu mới nhất. Luyện thêm đề để nâng cao điểm số nhé!'
        );
        setTimeout(() => setInfoMessage(null), 4000);
      } else {
        setProfile(result);
      }
    } catch (err: any) {
      setError(err.message || 'Không thể tạo hồ sơ');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      await catProfileService.downloadPdf();
    } catch (err: any) {
      setError(err.message || 'Không thể tải PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className='min-h-screen bg-background-light dark:bg-background-dark flex flex-col'>
        <SEOHead
          title='Hồ sơ năng lực CAT | Hướng nghiệp CAT'
          description='Hồ sơ năng lực cá nhân - Hướng nghiệp CAT.'
          noindex
        />
        <UnifiedHeader />
        <main className='flex-1 pt-20 pb-12 lg:pt-24 flex items-center justify-center'>
          <div className='text-center space-y-4'>
            <Loader2 className='w-12 h-12 animate-spin text-primary mx-auto' />
            <p className='text-subtext-light dark:text-subtext-dark text-lg'>
              Đang tải Hồ Sơ Năng Lực CAT...
            </p>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-background-light dark:bg-background-dark font-body text-gray-900 dark:text-gray-100 flex flex-col'>
      <SEOHead
        title='Hồ sơ năng lực CAT | Hướng nghiệp CAT'
        description='Hồ sơ năng lực cá nhân - Hướng nghiệp CAT.'
        noindex
      />
      <UnifiedHeader />

      <main className='flex-1 w-full max-w-7xl mx-auto pt-20 pb-12 lg:pt-24 px-4 sm:px-6 lg:px-8 flex flex-col gap-8'>
        {/* Error */}
        {error && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3'>
            <AlertCircle className='w-5 h-5 text-red-500 flex-shrink-0' />
            <p className='text-sm text-red-700 dark:text-red-300'>{error}</p>
          </div>
        )}

        {/* ============================================================ */}
        {/* SECTION 1: Bold amber→orange gradient banner                  */}
        {/* ============================================================ */}
        {eligibility && (
          <section className='rounded-xl overflow-hidden shadow-sm bg-gradient-to-r from-amber-400 to-orange-500 text-white p-6 md:p-8 flex flex-col gap-6'>
            <div className='flex flex-col lg:flex-row gap-8'>
              {/* Left: Description */}
              <div className='w-full lg:w-[40%] flex flex-col gap-4'>
                <h1 className='text-2xl lg:text-3xl font-bold'>Đánh giá chọn ngành</h1>
                <p className='text-white/90 leading-relaxed text-sm lg:text-base'>
                  Hãy tham gia &ldquo;Đánh giá chọn ngành&rdquo; để được định hướng dựa trên chính
                  dữ liệu của bạn. Chương trình kết hợp bài trắc nghiệm hướng nghiệp nhằm xác định
                  nhóm tính cách và sở thích nghề nghiệp, cùng với bài đánh giá năng lực các môn học
                  để đo lường năng lực học tập thực tế. Khi hoàn thành đầy đủ, bạn sẽ nhận được báo
                  cáo tư vấn giúp xác định ngành học phù hợp với cả sở trường và khả năng của mình,
                  từ đó đưa ra quyết định tự tin và có căn cứ hơn.
                </p>
              </div>

              {/* Right: Conditions white card */}
              <div className='w-full lg:w-[60%] bg-white dark:bg-gray-800 rounded-xl p-6 text-gray-900 dark:text-gray-100 flex flex-col gap-5'>
                <h3 className='font-semibold text-lg'>
                  Điều kiện để nhận báo cáo tư vấn ngành học đại học theo năng lực và sở trường:
                </h3>
                <div className='flex flex-col gap-4'>
                  {/* Condition 1: Career test */}
                  <div className='flex gap-3'>
                    {eligibility.hasFullReportAccess ? (
                      <CheckCircle2 className='w-6 h-6 text-green-500 flex-shrink-0 mt-0.5' />
                    ) : (
                      <div className='w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0 mt-0.5' />
                    )}
                    <div className='flex-1'>
                      <p className='font-medium'>Hoàn thành bài trắc nghiệm hướng nghiệp</p>
                      {eligibility.hasFullReportAccess ? (
                        <p className='text-sm text-green-600 dark:text-green-400'>Đã hoàn thành</p>
                      ) : (
                        <button
                          onClick={() => navigate('/huong-nghiep-cat?screen=instruction')}
                          className='text-blue-500 hover:underline text-sm'
                        >
                          → Khám phá bản thân
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Condition 2: Exam attempts */}
                  <div className='flex gap-3'>
                    {(eligibility.unifiedProgress?.current ?? 0) >=
                    (eligibility.unifiedProgress?.required ?? 3) ? (
                      <CheckCircle2 className='w-6 h-6 text-green-500 flex-shrink-0 mt-0.5' />
                    ) : (
                      <div className='w-6 h-6 rounded-full border-2 border-amber-500 flex-shrink-0 mt-0.5 flex items-center justify-center'>
                        <div className='w-2 h-2 rounded-full bg-amber-500' />
                      </div>
                    )}
                    <div className='flex-1 flex flex-col gap-3'>
                      <div className='flex justify-between items-center'>
                        <p className='font-medium'>
                          Hoàn thành ít nhất 3 lượt bài đánh giá năng lực bao gồm:
                        </p>
                        <span
                          className={`text-sm font-semibold ${
                            (eligibility.unifiedProgress?.current ?? 0) >=
                            (eligibility.unifiedProgress?.required ?? 3)
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-amber-500'
                          }`}
                        >
                          {eligibility.unifiedProgress?.current ?? 0}/
                          {eligibility.unifiedProgress?.required ?? 3}
                        </span>
                      </div>
                      <ul className='text-sm text-gray-500 dark:text-gray-400 space-y-1'>
                        <li>• 3 môn bất kì</li>
                        <li>
                          • 2 môn với tổng số lần thi là 3 lần (2 lần cho 1 môn và 1 lần cho 1 môn
                          khác)
                        </li>
                        <li>• 1 môn ít nhất 3 lần</li>
                      </ul>
                      {/* Progress bar */}
                      <div className='rounded-full bg-gray-100 dark:bg-gray-700 h-2 overflow-hidden'>
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${
                            (eligibility.unifiedProgress?.current ?? 0) >=
                            (eligibility.unifiedProgress?.required ?? 3)
                              ? 'bg-green-500'
                              : 'bg-amber-500'
                          }`}
                          style={{
                            width: `${Math.min(
                              ((eligibility.unifiedProgress?.current ?? 0) /
                                (eligibility.unifiedProgress?.required || 3)) *
                                100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                      {(eligibility.unifiedProgress?.current ?? 0) <
                        (eligibility.unifiedProgress?.required ?? 3) && (
                        <p className='text-sm text-amber-600 dark:text-amber-500 font-medium'>
                          Cần hoàn thành thêm{' '}
                          {(eligibility.unifiedProgress?.required ?? 3) -
                            (eligibility.unifiedProgress?.current ?? 0)}{' '}
                          bài thi nữa
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* CTA: Go practice */}
                {!eligibility.isEligible && (
                  <div className='mt-2'>
                    <button
                      onClick={() => navigate('/thi-thu-thpt')}
                      className='bg-primary hover:bg-primary/90 text-white px-6 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors w-full sm:w-auto'
                    >
                      Luyện đề THPT <span>→</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom bar: Report CTA  -  translucent on gradient */}
            <div className='bg-white/15 backdrop-blur-sm border border-white/20 rounded-lg p-5 flex flex-col sm:flex-row items-center justify-between gap-4 mt-2'>
              <div className='flex items-center gap-3'>
                {eligibility.isEligible ? (
                  <CheckCircle2 className='w-5 h-5 text-green-300' />
                ) : (
                  <AlertCircle className='w-5 h-5 text-white/80' />
                )}
                <p className='font-medium text-white/90'>
                  {eligibility.isEligible
                    ? 'Đủ điều kiện! Xem báo cáo tư vấn ngay:'
                    : 'Khi đủ điều kiện, bạn sẽ nhận được:'}
                </p>
              </div>
              {eligibility.isEligible && !profile ? (
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className='border border-white/50 hover:bg-white/10 text-white px-5 py-2 rounded-lg font-medium transition-colors whitespace-nowrap disabled:opacity-50'
                >
                  {generating ? (
                    <span className='flex items-center gap-2'>
                      <Loader2 className='w-4 h-4 animate-spin' />
                      Đang tạo báo cáo...
                    </span>
                  ) : (
                    '📄 Xem Báo cáo tư vấn lựa chọn ngành đại học theo năng lực và sở trường'
                  )}
                </button>
              ) : profile ? (
                <div className='flex gap-2'>
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className='border border-white/50 hover:bg-white/10 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm'
                  >
                    {generating ? (
                      <Loader2 className='w-3.5 h-3.5 animate-spin' />
                    ) : (
                      <RefreshCw className='w-3.5 h-3.5' />
                    )}
                    Cập nhật
                  </button>
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    className='border border-white/50 hover:bg-white/10 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm'
                  >
                    {downloading ? (
                      <Loader2 className='w-3.5 h-3.5 animate-spin' />
                    ) : (
                      <Download className='w-3.5 h-3.5' />
                    )}
                    Tải PDF
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={sharing}
                    className='border border-white/50 hover:bg-white/10 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm'
                  >
                    {sharing ? (
                      <Loader2 className='w-3.5 h-3.5 animate-spin' />
                    ) : (
                      <Share2 className='w-3.5 h-3.5' />
                    )}
                    Chia sẻ
                  </button>
                </div>
              ) : (
                <button className='border border-white/50 text-white px-5 py-2 rounded-lg font-medium whitespace-nowrap opacity-50 cursor-not-allowed'>
                  📄 Xem Báo cáo tư vấn lựa chọn ngành đại học theo năng lực và sở trường
                </button>
              )}
            </div>
          </section>
        )}

        {/* Info message (after banner) */}
        {infoMessage && (
          <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-center gap-3'>
            <AlertCircle className='w-5 h-5 text-amber-500 flex-shrink-0' />
            <p className='text-sm text-amber-700 dark:text-amber-300'>{infoMessage}</p>
          </div>
        )}

        {/* ============================================================ */}
        {/* SECTION 2: Two separate white cards side by side              */}
        {/* ============================================================ */}
        {profile && (
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
            {/* Left card: Exam scores table */}
            <div className='bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700'>
              <h3 className='text-lg font-bold mb-6 flex items-center gap-2'>
                <span className='material-icons-outlined text-2xl text-amber-500'>history_edu</span>
                Kết quả bài thi thử tốt nghiệp THPT
              </h3>
              <div className='overflow-x-auto'>
                <table className='w-full text-left border-collapse'>
                  <thead>
                    <tr className='border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-sm'>
                      <th className='py-3 px-4 font-medium'>Môn thi</th>
                      <th className='py-3 px-4 font-medium text-center'>Số lần</th>
                      <th className='py-3 px-4 font-medium'>Điểm từng lần</th>
                      <th className='py-3 px-4 font-medium text-right'>Trung bình</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100 dark:divide-gray-700/50'>
                    {profile.examScores.map(score => {
                      const maxScore = Math.max(...score.scores);
                      return (
                        <tr key={score.subject}>
                          <td className='py-4 px-4 font-medium'>{score.subjectName}</td>
                          <td className='py-4 px-4 text-center'>{score.attemptCount}</td>
                          <td className='py-4 px-4'>
                            <div className='flex gap-2 flex-wrap'>
                              {score.scores.map((s, i) => (
                                <span
                                  key={i}
                                  className={`px-2 py-1 text-xs rounded-md ${
                                    s === maxScore && score.scores.length > 1
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium'
                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                  }`}
                                >
                                  {s.toFixed(1)}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className='py-4 px-4 text-right font-semibold'>
                            {score.average.toFixed(1)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right card: Personality results (Holland scores) */}
            <div className='bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700'>
              <h3 className='text-lg font-bold mb-6 flex items-center gap-2'>
                <span className='material-icons-outlined text-2xl text-amber-500'>psychology</span>
                Kết quả bài trắc nghiệm hướng nghiệp
              </h3>
              <div className='flex flex-col gap-6'>
                {profile.personalityScores && profile.personalityScores.length > 0 ? (
                  profile.personalityScores.map(ps => {
                    const holland = hollandDescriptions[ps.groupCode];
                    const desc = hollandTexts[ps.groupCode] || '';
                    const percentage = Math.min((ps.score / Math.max(ps.maxScore, 1)) * 100, 100);

                    return (
                      <div key={ps.groupCode}>
                        <div className='flex justify-between items-center mb-2'>
                          <h4 className={`font-semibold ${holland?.color || 'text-blue-600'}`}>
                            {holland?.label || ps.groupName} ({holland?.english || ''})
                          </h4>
                          <span className='text-sm font-medium'>
                            {ps.score}/{ps.maxScore}
                          </span>
                        </div>
                        <div className='rounded-full bg-gray-100 dark:bg-gray-700 h-2 mb-2 overflow-hidden'>
                          <div
                            className={`h-full rounded-full ${holland?.barColor || 'bg-blue-500'} transition-all duration-700`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        {desc && <p className='text-sm text-gray-600 dark:text-gray-400'>{desc}</p>}
                      </div>
                    );
                  })
                ) : eligibility?.hasFullReportAccess ? (
                  <div className='text-center py-4'>
                    <p className='text-gray-500 dark:text-gray-400 mb-3'>
                      Hồ sơ cũ chưa có dữ liệu trắc nghiệm hướng nghiệp.
                    </p>
                    <button
                      onClick={handleGenerate}
                      disabled={generating}
                      className='inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50'
                    >
                      {generating ? (
                        <Loader2 className='w-4 h-4 animate-spin' />
                      ) : (
                        <RefreshCw className='w-4 h-4' />
                      )}
                      Cập nhật để xem kết quả
                    </button>
                  </div>
                ) : (
                  <div className='text-center py-4'>
                    <p className='text-gray-500 dark:text-gray-400 mb-3'>
                      Chưa có kết quả trắc nghiệm hướng nghiệp
                    </p>
                    <button
                      onClick={() => navigate('/huong-nghiep-cat?screen=instruction')}
                      className='inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-semibold transition-colors'
                    >
                      Làm trắc nghiệm hướng nghiệp
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* SECTION 3: Grouped majors in slate-50 card, 3-col grid       */}
        {/* ============================================================ */}
        {profile &&
          (() => {
            const groupsWithPassed = profile.recommendations.filter(g =>
              g.majors.some(m => m.passed)
            );
            const hasPassed = groupsWithPassed.length > 0;

            return (
              <section className='bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-700/50'>
                <div className='mb-6'>
                  <h3 className='text-xl font-bold mb-2'>Danh sách ngành học đại học phù hợp</h3>
                  {hasPassed ? (
                    <p className='text-gray-500 dark:text-gray-500 text-sm italic'>
                      Dựa trên {groupsWithPassed.length} nhóm tính cách nổi bật và điểm thi của bạn
                    </p>
                  ) : (
                    <p className='text-gray-500 dark:text-gray-500 text-sm italic'>
                      Dựa trên 3 nhóm tính cách nổi bật và điểm thi của bạn
                    </p>
                  )}
                </div>

                {hasPassed ? (
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                    {profile.recommendations.map(group => {
                      const passedMajors = group.majors.filter(m => m.passed);
                      if (passedMajors.length === 0) return null;

                      return (
                        <div key={group.groupCode} className='flex flex-col gap-3'>
                          <div className='flex items-center gap-2 mb-2'>
                            <div
                              className={`w-3 h-3 rounded-full ${hollandDescriptions[group.groupCode]?.barColor || 'bg-blue-500'}`}
                            />
                            <h4 className='font-semibold text-gray-800 dark:text-gray-200'>
                              Khối {group.groupName}
                            </h4>
                          </div>
                          {passedMajors.map(major => (
                            <button
                              key={major.majorId}
                              onClick={() =>
                                navigate(
                                  `/kham-pha-nghe-nghiep?career=${generateSlug(major.majorName)}`
                                )
                              }
                              className='bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 hover:border-primary dark:hover:border-primary transition-colors flex justify-between items-center group text-left'
                            >
                              <span className='font-medium text-sm'>{major.majorName}</span>
                              <span className='text-gray-400 group-hover:text-primary transition-colors text-sm'>
                                →
                              </span>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className='bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-100 dark:border-gray-700'>
                    <div className='w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4'>
                      <span className='material-icons-outlined text-3xl text-amber-500'>
                        trending_up
                      </span>
                    </div>
                    <h4 className='font-bold text-lg text-gray-900 dark:text-white mb-2'>
                      Điểm thi chưa đạt ngưỡng gợi ý ngành
                    </h4>
                    <p className='text-sm text-gray-600 dark:text-gray-400 max-w-lg mx-auto mb-5'>
                      Hiện tại điểm trung bình các môn thi của bạn chưa đạt ngưỡng tối thiểu để gợi
                      ý ngành học. Hãy luyện thêm đề thi để nâng cao điểm số - mỗi lần thi lại sẽ
                      cập nhật điểm trung bình và mở ra thêm nhiều ngành phù hợp!
                    </p>
                    <button
                      onClick={() => navigate('/thi-thu-thpt')}
                      className='inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold transition-colors shadow-md'
                    >
                      <Play className='w-4 h-4' />
                      Luyện đề ngay để nâng điểm
                    </button>
                  </div>
                )}
              </section>
            );
          })()}

        {/* No profile and not eligible  -  attractive locked preview */}
        {!profile && eligibility && !eligibility.isEligible && (
          <div className='relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden'>
            {/* Blurred sample data behind */}
            <div className='blur-[4px] select-none pointer-events-none p-6'>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
                {/* Sample exam scores */}
                <div>
                  <h3 className='text-lg font-bold mb-4 flex items-center gap-2'>
                    <span className='material-icons-outlined text-2xl text-amber-500'>
                      history_edu
                    </span>
                    Kết quả bài thi thử tốt nghiệp THPT
                  </h3>
                  <table className='w-full text-left'>
                    <thead>
                      <tr className='border-b border-gray-200 text-gray-500 text-sm'>
                        <th className='py-2 font-medium'>Môn thi</th>
                        <th className='py-2 font-medium text-center'>Số lần</th>
                        <th className='py-2 font-medium'>Điểm từng lần</th>
                        <th className='py-2 font-medium text-right'>TB</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100'>
                      {[
                        { name: 'Toán', count: 5, scores: [8.5, 7.2, 9.0, 6.8, 8.1], avg: 7.9 },
                        { name: 'Tiếng Anh', count: 3, scores: [7.8, 8.4, 7.5], avg: 7.9 },
                        { name: 'Vật lí', count: 3, scores: [8.2, 7.6, 8.8], avg: 8.2 },
                      ].map(s => (
                        <tr key={s.name}>
                          <td className='py-3 font-medium'>{s.name}</td>
                          <td className='py-3 text-center'>{s.count}</td>
                          <td className='py-3'>
                            <div className='flex gap-1.5 flex-wrap'>
                              {s.scores.map((sc, i) => (
                                <span
                                  key={i}
                                  className='px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700'
                                >
                                  {sc}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className='py-3 text-right font-semibold'>{s.avg}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Sample personality scores */}
                <div>
                  <h3 className='text-lg font-bold mb-4 flex items-center gap-2'>
                    <span className='material-icons-outlined text-2xl text-amber-500'>
                      psychology
                    </span>
                    Kết quả bài trắc nghiệm hướng nghiệp
                  </h3>
                  <div className='space-y-4'>
                    {[
                      {
                        label: 'KỸ THUẬT (Realistic)',
                        score: 7,
                        color: 'bg-blue-500',
                        width: '87.5%',
                      },
                      {
                        label: 'NGHIÊN CỨU (Investigative)',
                        score: 6,
                        color: 'bg-purple-500',
                        width: '75%',
                      },
                      {
                        label: 'QUẢN LÝ (Enterprising)',
                        score: 5,
                        color: 'bg-emerald-500',
                        width: '62.5%',
                      },
                    ].map(g => (
                      <div key={g.label}>
                        <div className='flex justify-between items-center mb-1'>
                          <span className='font-semibold text-blue-600 text-sm'>{g.label}</span>
                          <span className='text-sm font-medium'>{g.score}/8</span>
                        </div>
                        <div className='h-2 bg-gray-100 rounded-full overflow-hidden'>
                          <div
                            className={`h-full rounded-full ${g.color}`}
                            style={{ width: g.width }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sample majors */}
              <div className='mt-6 pt-6 border-t border-gray-100'>
                <h3 className='text-lg font-bold mb-2'>Danh sách ngành học đại học phù hợp</h3>
                <p className='text-gray-500 text-sm italic mb-4'>
                  Dựa trên 3 nhóm tính cách nổi bật và điểm thi của bạn
                </p>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                  <div>
                    <div className='flex items-center gap-2 mb-3'>
                      <div className='w-3 h-3 rounded-full bg-blue-500' />
                      <h4 className='font-semibold'>Khối KỸ THUẬT</h4>
                    </div>
                    <div className='space-y-2'>
                      {[
                        'Công nghệ thông tin',
                        'Khoa học máy tính',
                        'Kỹ thuật phần mềm',
                        'An toàn thông tin',
                        'Điều khiển & tự động hóa',
                        'Kỹ thuật điện',
                      ].map(m => (
                        <div
                          key={m}
                          className='bg-gray-50 p-3 rounded-lg text-sm font-medium flex justify-between items-center'
                        >
                          {m} <span className='text-gray-400'>→</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className='flex items-center gap-2 mb-3'>
                      <div className='w-3 h-3 rounded-full bg-purple-500' />
                      <h4 className='font-semibold'>Khối NGHIÊN CỨU</h4>
                    </div>
                    <div className='space-y-2'>
                      {[
                        'Trí tuệ nhân tạo',
                        'Khoa học dữ liệu',
                        'Toán học',
                        'Vật lý học',
                        'Kỹ thuật y sinh',
                      ].map(m => (
                        <div
                          key={m}
                          className='bg-gray-50 p-3 rounded-lg text-sm font-medium flex justify-between items-center'
                        >
                          {m} <span className='text-gray-400'>→</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className='flex items-center gap-2 mb-3'>
                      <div className='w-3 h-3 rounded-full bg-emerald-500' />
                      <h4 className='font-semibold'>Khối QUẢN LÝ</h4>
                    </div>
                    <div className='space-y-2'>
                      {[
                        'Quản trị kinh doanh',
                        'Tài chính – Ngân hàng',
                        'Marketing',
                        'Kinh doanh quốc tế',
                        'Logistics & Chuỗi cung ứng',
                      ].map(m => (
                        <div
                          key={m}
                          className='bg-gray-50 p-3 rounded-lg text-sm font-medium flex justify-between items-center'
                        >
                          {m} <span className='text-gray-400'>→</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lock overlay */}
            <div className='absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/40 via-white/70 to-white/90 dark:from-gray-900/40 dark:via-gray-900/70 dark:to-gray-900/90'>
              <div className='text-center px-6 max-w-md'>
                <div className='w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-200/50'>
                  <Lock className='w-8 h-8 text-amber-600 dark:text-amber-400' />
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                  Mở khóa báo cáo tư vấn chọn ngành
                </h3>
                <p className='text-sm text-gray-600 dark:text-gray-400 mb-1'>
                  Hoàn thành bài trắc nghiệm hướng nghiệp và thi thử tối thiểu 3 lượt để nhận:
                </p>
                <ul className='text-sm text-gray-600 dark:text-gray-400 mb-5 space-y-1'>
                  <li className='flex items-center gap-2 justify-center'>
                    <CheckCircle2 className='w-4 h-4 text-green-500 flex-shrink-0' />
                    Phân tích điểm thi chi tiết theo từng môn
                  </li>
                  <li className='flex items-center gap-2 justify-center'>
                    <CheckCircle2 className='w-4 h-4 text-green-500 flex-shrink-0' />
                    Xác định 3 nhóm tính cách nổi bật của bạn
                  </li>
                  <li className='flex items-center gap-2 justify-center'>
                    <CheckCircle2 className='w-4 h-4 text-green-500 flex-shrink-0' />
                    Gợi ý ngành đại học phù hợp nhất
                  </li>
                  <li className='flex items-center gap-2 justify-center'>
                    <CheckCircle2 className='w-4 h-4 text-green-500 flex-shrink-0' />
                    Tải PDF báo cáo tư vấn hoàn toàn miễn phí
                  </li>
                </ul>
                <div className='flex gap-3 justify-center'>
                  {!eligibility.hasFullReportAccess && (
                    <button
                      onClick={() => navigate('/huong-nghiep-cat?screen=instruction')}
                      className='flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors shadow-md whitespace-nowrap'
                    >
                      <span className='material-icons-outlined text-lg leading-none'>
                        psychology
                      </span>
                      Trắc nghiệm hướng nghiệp
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/thi-thu-thpt')}
                    className='flex items-center justify-center gap-2 px-5 py-3 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-colors shadow-md whitespace-nowrap'
                  >
                    <Play className='w-4 h-4 flex-shrink-0' />
                    Thi thử ngay
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Share Modal */}
      {showShareModal && shareToken && (
        <div
          className='fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4'
          onClick={() => setShowShareModal(false)}
        >
          <div
            className='bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4'
            onClick={e => e.stopPropagation()}
          >
            <div className='flex items-center justify-between'>
              <h3 className='text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2'>
                <LinkIcon className='w-5 h-5 text-primary' />
                Chia sẻ hồ sơ
              </h3>
              <button
                onClick={() => setShowShareModal(false)}
                className='p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800'
              >
                <X className='w-5 h-5 text-gray-500' />
              </button>
            </div>
            <p className='text-sm text-gray-600 dark:text-gray-400'>
              Bất kỳ ai có link đều xem được hồ sơ năng lực của bạn mà không cần đăng nhập.
            </p>
            <div className='flex items-center gap-2'>
              <input
                readOnly
                value={getShareUrl(shareToken)}
                className='flex-1 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-gray-700 dark:text-gray-300 font-mono'
              />
              <button
                onClick={handleCopyLink}
                className='flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors shrink-0'
              >
                {copied ? <Check className='w-4 h-4' /> : <Copy className='w-4 h-4' />}
                {copied ? 'Đã sao chép!' : 'Sao chép'}
              </button>
            </div>
            <div className='flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-800'>
              <button
                onClick={handleDisableShare}
                className='text-sm text-red-500 hover:text-red-600 font-medium'
              >
                Tắt chia sẻ
              </button>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl(shareToken))}`}
                target='_blank'
                rel='noopener noreferrer'
                className='text-sm text-blue-600 hover:text-blue-700 font-medium'
              >
                Chia sẻ lên Facebook →
              </a>
            </div>
          </div>
        </div>
      )}

      <UnifiedFooter />
    </div>
  );
}
